import { Pill } from '@mantine/core';
import type { ReactNode } from 'react';
import { HelpContent, HelpContentHeading } from './HelpContent';

export function TrappedDefinition(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Public trapped</h4>
			</HelpContentHeading>

			<p>
				When someone is trapped or confined due to DTE's equipment and/or infrastructure such as wire down or
				pole down.
			</p>
		</HelpContent>
	);
}

export function ContactDefinition(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Contact with electrical equipment</h4>
				<Pill.Group>
					<Pill>Public contact</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>When someone has received an electric shock, resulting in either an injury or a fatality.</p>
			<p>Such incidents are critical and require immediate attention due to the severe consequences involved.</p>
		</HelpContent>
	);
}

export function DownWireDefinition(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Downed electrical wire</h4>
				<Pill.Group>
					<Pill>Down wire</Pill>
					<Pill>Wire down</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				When a wire that typically runs from pole to pole, or from a utility pole to a building, has fallen to
				the ground. These wires may be part of overhead power lines and can pose significant safety risks when
				they are no longer suspended as intended.
			</p>
			<p>
				The presence of a downed wire in the vicinity of a school, park, or playground presents a significant
				danger to children and other members of the community who frequent these areas.
			</p>
		</HelpContent>
	);
}

export function WireWithinReachDefinition(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Wire within reach</h4>
				<Pill.Group>
					<Pill>Low wire</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				This situation occurs when a wire running from one pole to another, or from a pole to a building, is
				hanging low enough that it can be touched by people. The accessibility of these wires increases the
				likelihood of accidental contact, which can result in electric shock or other hazards. It is important
				to identify and address any electrical wires that are not properly elevated to ensure the safety of the
				area.
			</p>
			<p>
				The presence of a low hanging wire in the vicinity of a school, park, or playground presents a
				significant danger to children and other members of the community who frequent these areas.
			</p>
		</HelpContent>
	);
}

export function ExposedUndergroundDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Underground lines exposed</h4>
			</HelpContentHeading>
			<p>
				When buried electrical lines are no longer completely covered by soil or other materials. In these
				situations, the lines themselves become visible, or a portion of the line may be exposed above ground.
			</p>
			<p>
				<b>Common causes of exposure</b>
			</p>
			<ul>
				<li>
					<b>Digging:</b> Excavation activities can disturb the soil, uncovering previously buried lines.
				</li>
				<li>
					<b>Erosion:</b> Natural processes, such as water runoff, can wear away the covering material,
					revealing underground lines.
				</li>
				<li>
					<b>Construction:</b> Building projects or site development may result in the exposure of utility
					lines.
				</li>
			</ul>
		</HelpContent>
	);
}

export function MissingManholeDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Manhole cover missing</h4>
				<Pill.Group>
					<Pill>Missing manhole</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>When the protective lid that is designed to cover an underground utility opening is missing.</p>
			<p>
				An open manhole poses a danger to anyone in the vicinity, since people or vehicles could accidentally
				fall into the open space below.
			</p>
		</HelpContent>
	);
}

export function ObjectOnWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Object on wire</h4>
				<Pill.Group>
					<Pill>Branches on wire</Pill>
					<Pill>Tree on wire</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>An object sitting on or touching a power line that does not belong there.</p>
			<p>This can include:</p>
			<ul>
				<li>
					Natural materials, such as:
					<ul>
						<li>
							<b>Branches</b>
						</li>
						<li>
							<b>Trees</b>
						</li>
					</ul>
				</li>
				<li>
					Man-made items like:
					<ul>
						<li>
							<b>Balloons</b>
						</li>
						<li>
							<b>Debris</b>
						</li>
					</ul>
				</li>
			</ul>
			<p>Risks:</p>
			<ul>
				<li>
					Damage to equipment
					<ul>
						<li>
							<b>Wires may be damaged</b>, sag, or break
						</li>
					</ul>
				</li>
				<li>
					Unsafe conditions
					<ul>
						<li>
							<b>Short circuits</b>
						</li>
						<li>
							<b>Fires</b>
						</li>
					</ul>
				</li>
				<li>
					Service interruptions
					<ul>
						<li>
							<b>Loss of power</b>
						</li>
						<li>
							<b>Flickering</b> lights
						</li>
					</ul>
				</li>
			</ul>
		</HelpContent>
	);
}

export function TrafficSignalOutDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Traffic signal out</h4>
			</HelpContentHeading>
			<p>When a stoplight loses power or malfunctions completely, resulting in all the lights being off.</p>
			<p>
				In this situation, none of the signal lights—red, yellow, or green—are illuminated, indicating that the
				signal is not operational.
			</p>
		</HelpContent>
	);
}

export function TransformerPoleNoiseDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Noise at pole</h4>
				<Pill.Group>
					<Pill>Transformer noise</Pill>
				</Pill.Group>
			</HelpContentHeading>

			<p>
				Transformer and pole-mounted electrical equipment generally produce a consistent, low-level hum during
				normal operation.
			</p>

			<p>Potential indicators that something inside equipment is loose or not functioning properly:</p>
			<ul>
				<li>
					<b>Sudden increases</b> in noise level
				</li>
				<li>
					<b>Sharp or unusual sounds</b>
				</li>
				<li>
					<b>Abrupt changes</b> in the usual operating noise
				</li>
			</ul>

			<p>
				These changes in sound can serve as early warning signs of issues that require inspection or
				maintenance.
			</p>
		</HelpContent>
	);
}

export function LowWireHazardDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Low wire</h4>
			</HelpContentHeading>
			<p>When an electrical line is hanging lower than normal, but out of reach.</p>
			<p>As long as the wire remains out of reach, it does not pose an immediate contact hazard.</p>
		</HelpContent>
	);
}

export function FrayingWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Fraying wire</h4>
			</HelpContentHeading>
			<p>When the protective insulation surrounding the wire has been compromised or damaged.</p>
			<p>If the damage is severe, the metal conductor inside the wire may be exposed.</p>
		</HelpContent>
	);
}

export function BrokenGuyWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Broken guy wire</h4>
			</HelpContentHeading>
			<p>
				Guy wires are strong, tensioned steel cables designed to stabilize utility poles. They prevent the poles
				from tipping over due to the wind or the weight of wires and mounted equipment.
			</p>
			<p>
				A guy wire that snaps or comes loose can lead to a pole leaning or breaking, which can pose a risk to
				public safety and interrupt electrical service.
			</p>
		</HelpContent>
	);
}

export function ElectricityAllOutDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Power outage</h4>
				<Pill.Group>
					<Pill>Electricity is all out</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				When all sources of electricity in a home or building cease to function, resulting in a total loss of
				electrical supply throughout the entire structure.
			</p>
			<p>
				During a complete power outage, every device or system dependent on electricity becomes inoperative,
				including:
			</p>
			<ul>
				<li>Lighting fixtures</li>
				<li>Household appliances</li>
				<li>Heating systems (such as furnaces)</li>
				<li>Electrical outlets</li>
				<li>Internet equipment (such as routers and modems)</li>
				<li>Garage door openers</li>
			</ul>
		</HelpContent>
	);
}

export function PartialPowerDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Partial power</h4>
				<Pill.Group>
					<Pill>One leg out</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>When only specific circuits, outlets, or rooms within a home lose electrical power.</p>
			<p>
				In contrast to a complete outage, some circuits, rooms, or outlets may still continue to have power and
				operate as usual.
			</p>
		</HelpContent>
	);
}

export function InterruptibleServiceDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Interruptible service</h4>
				<Pill.Group>
					<Pill>Cool Currents</Pill>
					<Pill>Interruptible air conditioning (IAC)</Pill>
					<Pill>Interruptible water heating (IWH)</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<div>
				<p>
					Residential and Commercial customers desiring separately metered interruptible service for central
					air conditioning and/or central heat pump use. DTE opts to interrupt customer equipment associated
					with this rate in a pattern of 15 minutes of service interruption followed by 15 minutes of the
					appliance operating.
				</p>
				<p>
					If only the IAC or IWH is without power, confirm that the meters are not interrupted by the Cool
					Currents process before an outage order is created.
				</p>
			</div>
		</HelpContent>
	);
}

export function BrightToDimLightsDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Low voltage</h4>
				<Pill.Group>
					<Pill>high voltage</Pill>
					<Pill>bright to dim lights</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				When customers notice the lights in their home becoming bright and then dim, this typically indicates
				that the voltage entering the home is not stable. Such voltage fluctuations can occur for several
				reasons.
			</p>
			<p>
				<b>Possible reasons for voltage fluctuations:</b>
			</p>
			<ul>
				<li>
					<b>Loose electrical connections:</b> A loose connection within the electrical system can cause
					inconsistent voltage delivery, resulting in noticeable changes in the light brightness.
				</li>
				<li>
					<b>Neutral wire issues:</b> Problems with the neutral wire, which is responsible for carrying
					current back to the electrical panel, can also lead to voltage swings that affect the lighting.
				</li>
				<li>
					<b>Utility line problems:</b> Sometimes, the issue originates outside the home. If there is a fault
					or fluctuation on the utility lines, this can cause the voltage in the home to rise and fall, making
					the lights appear brighter or dimmer.
				</li>
			</ul>
		</HelpContent>
	);
}

export function FlickeringLightsDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Flickering lights</h4>
			</HelpContentHeading>
			<p>Flickering lights are a sign that electricity is not flowing smoothly through the electrical system.</p>
			<p>
				<b>Common causes of flickering lights:</b>
			</p>
			<ul>
				<li>
					<b>Loose bulb or fixture:</b> If a light bulb or its fixture is not securely fastened, it may cause
					intermittent contact
				</li>
				<li>
					<b>Bad switch or dimmer:</b> Faulty switches or dimmers can disrupt the flow of electricity
				</li>
				<li>
					<b>LED lights on dimmers:</b> Dimming LED lights requires both compatible bulbs and dimmers
				</li>
				<li>
					<b>Excessive load:</b> Too many devices connected to a single circuit can overload it
				</li>
				<li>
					<b>Loose wiring:</b> Wiring that is not properly connected or has become loose over time
				</li>
				<li>
					<b>Voltage fluctuations:</b> Changes in voltage levels due to issues with the electrical supply
				</li>
			</ul>
		</HelpContent>
	);
}

export function BrokenOrLeaningPoleDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Broken or leaning pole</h4>
				<Pill.Group>
					<Pill>Damaged pole</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>When a pole has suffered damage or is tilting beyond what is considered normal.</p>
			<p>Such conditions may compromise the pole's stability and pose potential safety hazards.</p>
		</HelpContent>
	);
}

export function PoleTransformerFireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Pole or transformer fire</h4>
			</HelpContentHeading>
			<p>A transformer mounted on a utility pole that has caught on fire.</p>
			<p>
				When components inside the transformer malfunction or break down, it can result in overheating or
				sparks, ultimately leading to a fire.
			</p>
			<p>Recognizing the cause of such a fire is crucial for both safety and timely response.</p>
		</HelpContent>
	);
}

export function ArcingWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Arcing wire</h4>
				<Pill.Group>
					<Pill>Sparking wire</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				When a power line is sparking or flashing as electricity jumps across a gap from one point to another.
			</p>
			<p>Arcing can occur when a wire is damaged or comes into contact with a foreign object.</p>
		</HelpContent>
	);
}

export function TreeInContactWithWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Tree in contact with wire</h4>
			</HelpContentHeading>
			<p>
				When a branch or an entire tree comes into contact with a power line, it creates a direct connection
				between the tree and the wire. This situation can result in several electrical issues.
			</p>
			<p>
				<b>Potential Consequences</b>
			</p>
			<ul>
				<li>
					<strong>Flickering lights:</strong> The contact may cause fluctuations in the electrical supply,
					leading to lights that flicker or dim unexpectedly.
				</li>
				<li>
					<strong>Sparks:</strong> In more severe cases, the tree touching the power line can generate sparks,
					which pose safety hazards.
				</li>
			</ul>
		</HelpContent>
	);
}

export function TreesGrowingIntoWiresDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Trees growing into wires</h4>
			</HelpContentHeading>
			<p>
				When trees grow near power lines, their branches can gradually extend toward the wires. As the branches
				get closer, there is an increased risk that they will come into direct contact with the electrical
				lines. This contact can result in electrical problems, such as flickering lights or even sparking. These
				issues occur because the branches interfere with the flow of electricity, potentially leading to
				dangerous situations or temporary power disruptions.
			</p>
		</HelpContent>
	);
}

export function DebrisLeftBehindByDTEDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Debris left behind by DTE</h4>
			</HelpContentHeading>
			<p>
				After the completion of work by the DTE crews, it was observed that the job site was not fully cleaned.
				Although the main project tasks were finished, various materials remained scattered throughout the area.
				These may include branches, dirt, wires, and scraps of equipment, which were not removed during the
				final cleanup process. The presence of such debris indicates that additional attention to site
				cleanliness may be required to ensure a fully restored environment.
			</p>
		</HelpContent>
	);
}

export function PoleLeftInDitchOrYardDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Pole left in ditch or yard</h4>
			</HelpContentHeading>
			<p>
				When DTE install new poles, the expectation is that the old poles will be removed promptly. However, in
				some cases, the old utility pole remains at the site even after its replacement. This situation is
				referred to as a "utility pole left behind," which means the previous pole was not taken down following
				the installation of the new one.
			</p>
		</HelpContent>
	);
}

export function GLNDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Global Location Number (GLN)</h4>
			</HelpContentHeading>
			<p>A unique 12-digit number similar to GPS.</p>
			<ul>
				<li>The first 6 digits of a GLN correspond to a longitude</li>
				<li>The second 6 digits of a GLN correspond to a latitude</li>
			</ul>
		</HelpContent>
	);
}
